import React, { useEffect, useRef, createElement } from 'react';
interface VideoHLSProps {
  src: string;
  poster?: string;
  className?: string;
  title?: string;
}
// Loads hls.js from a CDN once and resolves with the global Hls constructor.
let hlsLoaderPromise: Promise<any> | null = null;
function loadHlsJs(): Promise<any> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  // @ts-ignore — Hls is attached to window at runtime by the CDN script
  if ((window as any).Hls) return Promise.resolve((window as any).Hls);
  if (hlsLoaderPromise) return hlsLoaderPromise;
  hlsLoaderPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.13/dist/hls.min.js';
    script.async = true;
    script.onload = () => {
      // @ts-ignore
      resolve((window as any).Hls);
    };
    script.onerror = () => reject(new Error('Failed to load hls.js'));
    document.head.appendChild(script);
  });
  return hlsLoaderPromise;
}
/**
 * VideoHLS — plays an HLS (.m3u8) stream in any modern browser.
 * - Safari/iOS: uses native HLS support.
 * - Chrome/Firefox/Edge: loads hls.js from CDN and attaches the stream.
 */
export function VideoHLS({
  src,
  poster,
  className,
  title = 'Video player'
}: VideoHLSProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let hlsInstance: any = null;
    let cancelled = false;
    const isHls = /\.m3u8(\?|$)/i.test(src);
    // Non-HLS sources (mp4, webm, etc.) — just set src directly.
    if (!isHls) {
      video.src = src;
      return () => {
        cancelled = true;
      };
    }
    // Native HLS (Safari, iOS)
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      return () => {
        if (!cancelled) {
          video.removeAttribute('src');
          video.load();
        }
        cancelled = true;
      };
    }
    // Everyone else — use hls.js
    loadHlsJs().then(Hls => {
      if (cancelled || !videoRef.current) return;
      if (Hls.isSupported()) {
        hlsInstance = new Hls({
          enableWorker: true
        });
        hlsInstance.loadSource(src);
        hlsInstance.attachMedia(videoRef.current);
      } else {
        // Last resort — try setting src directly
        videoRef.current.src = src;
      }
    }).catch(() => {
      // If hls.js fails to load, fall back to direct src (will likely fail
      // outside Safari but it's the most graceful option).
      if (!cancelled && videoRef.current) {
        videoRef.current.src = src;
      }
    });
    return () => {
      cancelled = true;
      if (hlsInstance) {
        hlsInstance.destroy();
        hlsInstance = null;
      }
    };
  }, [src]);
  return <video ref={videoRef} poster={poster} controls playsInline preload="metadata" title={title} className={className} data-id="element-443">
      Your browser does not support video playback.
    </video>;
}